import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CheckCircle, ChevronRight, ChevronLeft, GraduationCap, BookOpen, CreditCard, Video, FileText, Clock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

/**
 * Multi-step onboarding wizard.
 * Pass mode="student" for Student profile→payment flow.
 * Pass mode="teacher" for Teacher demo→onboarding→availability flow.
 * onComplete fires when the user finishes the last step.
 */
export const OnboardingWizard = ({ open, onClose, mode = 'student', user, onComplete }) => {
  const isStudent = mode === 'student';
  const steps = isStudent
    ? [
        { id: 'profile', title: 'Your profile', icon: GraduationCap },
        { id: 'verticals', title: 'Choose your tracks', icon: BookOpen },
        { id: 'payment', title: 'Enrollment', icon: CreditCard },
        { id: 'done', title: 'You are ready', icon: CheckCircle },
      ]
    : [
        { id: 'demo', title: 'Demo class', icon: Video },
        { id: 'onboarding', title: 'Bio & skills', icon: FileText },
        { id: 'availability', title: 'Set availability', icon: Clock },
        { id: 'done', title: 'Awaiting admin approval', icon: CheckCircle },
      ];
  const [stepIdx, setStepIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // null | true | false
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [form, setForm] = useState({
    grade: user?.grade || '',
    timezone: user?.timezone || 'IST',
    verticals: Array.isArray(user?.verticals) ? user.verticals : [],
    bio: user?.bio || '',
    skills: Array.isArray(user?.skills) ? user.skills.join(', ') : '',
  });

  const verticalOptions = ['Coding', 'Robotics', 'Mathematics', 'AI', 'IELTS', 'Spoken Mastery'];

  const toggleVertical = (v) => {
    setForm(f => ({ ...f, verticals: f.verticals.includes(v) ? f.verticals.filter(x => x !== v) : [...f.verticals, v] }));
  };

  const saveStudentProfile = async () => {
    setSaving(true);
    try {
      await api.put('/student/settings', { grade: form.grade, timezone: form.timezone, verticals: form.verticals });
      toast.success('Profile saved');
      return true;
    } catch { toast.error('Save failed'); return false; }
    finally { setSaving(false); }
  };

  const saveTeacherOnboarding = async () => {
    setSaving(true);
    try {
      const skills = form.skills.split(',').map(s => s.trim()).filter(Boolean);
      await api.put('/teacher/onboarding', { bio: form.bio, skills, onboarding_complete: true });
      toast.success('Onboarding saved');
      return true;
    } catch { toast.error('Save failed'); return false; }
    finally { setSaving(false); }
  };

  const checkPaymentStatus = async () => {
    setCheckingPayment(true);
    try {
      const res = await api.get('/student/onboarding-status');
      setPaymentStatus(!!res.data?.payment_received);
    } catch { setPaymentStatus(false); }
    setCheckingPayment(false);
  };

  // Auto-poll payment status when user is on the payment step
  useEffect(() => {
    if (!open || !isStudent) return;
    if (steps[stepIdx]?.id !== 'payment') return;
    checkPaymentStatus();
    const interval = setInterval(checkPaymentStatus, 15000); // poll every 15s
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stepIdx, isStudent]);

  const handleNext = async () => {
    const current = steps[stepIdx].id;
    let ok = true;
    if (isStudent) {
      if (current === 'profile' && !form.grade) { toast.error('Please enter your grade'); return; }
      if (current === 'verticals') {
        if (form.verticals.length === 0) { toast.error('Pick at least one track'); return; }
        ok = await saveStudentProfile();
      }
      if (current === 'payment' && paymentStatus === false) {
        toast.error('Payment not yet received. Admin will mark your enrollment as paid soon.');
        return;
      }
    } else if (current === 'onboarding') {
      ok = await saveTeacherOnboarding();
    }
    if (!ok) return;
    if (stepIdx === steps.length - 1) {
      onComplete && onComplete();
      onClose && onClose();
    } else {
      setStepIdx(stepIdx + 1);
    }
  };

  const current = steps[stepIdx];
  const Icon = current.icon;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden border-2 border-slate-900 rounded-2xl shadow-[6px_6px_0_0_#0f172a]" data-testid="onboarding-wizard">
        {/* Header — stepper */}
        <div className="bg-gradient-to-r from-blue-50 to-emerald-50 p-5 border-b-2 border-slate-900">
          <div className="flex items-center gap-2 mb-3">
            {steps.map((s, idx) => (
              <React.Fragment key={s.id}>
                <div className={`flex-1 h-2 rounded-full ${idx <= stepIdx ? 'bg-blue-600' : 'bg-slate-200'}`} />
              </React.Fragment>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold shadow-[2px_2px_0_0_#0f172a]">
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Step {stepIdx + 1} of {steps.length}</p>
              <h2 className="text-lg font-bold text-slate-900">{current.title}</h2>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4" data-testid={`onboarding-step-${current.id}`}>
          {isStudent && current.id === 'profile' && (
            <>
              <div>
                <Label className="text-xs font-bold text-slate-700">Grade</Label>
                <Input value={form.grade} onChange={e => setForm({ ...form, grade: e.target.value })} placeholder="e.g. 5" className="mt-1" data-testid="onboarding-grade" />
              </div>
              <div>
                <Label className="text-xs font-bold text-slate-700">Timezone</Label>
                <Input value={form.timezone} onChange={e => setForm({ ...form, timezone: e.target.value })} placeholder="IST / PST / EST" className="mt-1" data-testid="onboarding-timezone" />
              </div>
            </>
          )}
          {isStudent && current.id === 'verticals' && (
            <div>
              <Label className="text-xs font-bold text-slate-700 mb-2 block">Pick the tracks you want to learn</Label>
              <div className="grid grid-cols-2 gap-2">
                {verticalOptions.map(v => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => toggleVertical(v)}
                    className={`p-3 rounded-xl border-2 text-sm font-bold transition-all ${form.verticals.includes(v) ? 'bg-blue-600 text-white border-slate-900 shadow-[2px_2px_0_0_#0f172a]' : 'bg-white border-slate-300 text-slate-700 hover:border-slate-900'}`}
                    data-testid={`onboarding-vertical-${v}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          )}
          {isStudent && current.id === 'payment' && (
            <div className="space-y-3">
              {paymentStatus === true ? (
                <div className="p-4 rounded-xl bg-emerald-50 border-2 border-emerald-300" data-testid="payment-received">
                  <div className="flex items-center gap-2 text-emerald-700 font-bold">
                    <CheckCircle className="w-5 h-5" strokeWidth={2.5} /> Payment received — you can schedule classes now
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-700">Class scheduling unlocks once payment is received. You can pay via:</p>
                  <ul className="text-sm text-slate-600 space-y-1 ml-4">
                    <li>• UPI transfer (ask admin for the QR)</li>
                    <li>• Bank transfer (admin will share details)</li>
                    <li>• Online payment gateway (coming soon)</li>
                  </ul>
                  <div className="p-3 rounded-xl bg-amber-50 border-2 border-amber-300 flex items-center justify-between gap-2" data-testid="payment-pending">
                    <div>
                      <p className="text-xs font-bold text-amber-700">Waiting for admin to mark payment received…</p>
                      <p className="text-[11px] text-amber-600">This page auto-refreshes every 15 seconds.</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={checkPaymentStatus} disabled={checkingPayment} className="flex-shrink-0" data-testid="payment-recheck">
                      <RefreshCw className={`w-3 h-3 mr-1 ${checkingPayment ? 'animate-spin' : ''}`} /> Check now
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
          {isStudent && current.id === 'done' && (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-2" strokeWidth={2.5} />
              <p className="text-slate-900 font-bold text-lg">You're all set!</p>
              <p className="text-sm text-slate-600 mt-1">Once admin marks payment as received, you can start booking classes.</p>
            </div>
          )}
          {!isStudent && current.id === 'demo' && (
            <div className="space-y-2 text-sm text-slate-700">
              <p>Complete your demo class first. Admin will mark it as done in your profile once observed.</p>
              <p className="text-xs text-slate-500">No action needed here — proceed once your demo has been recorded by admin.</p>
            </div>
          )}
          {!isStudent && current.id === 'onboarding' && (
            <>
              <div>
                <Label className="text-xs font-bold text-slate-700">Short bio</Label>
                <textarea
                  value={form.bio}
                  onChange={e => setForm({ ...form, bio: e.target.value })}
                  placeholder="Tell us about your teaching experience (2-3 lines)"
                  className="mt-1 w-full p-2 border-2 border-slate-300 rounded-xl text-sm focus:border-slate-900 outline-none min-h-[80px]"
                  data-testid="onboarding-bio"
                />
              </div>
              <div>
                <Label className="text-xs font-bold text-slate-700">Skills (comma-separated)</Label>
                <Input value={form.skills} onChange={e => setForm({ ...form, skills: e.target.value })} placeholder="Scratch, Python, Web Dev" className="mt-1" data-testid="onboarding-skills" />
              </div>
            </>
          )}
          {!isStudent && current.id === 'availability' && (
            <div className="space-y-2 text-sm text-slate-700">
              <p>Set up your weekly availability slots — head to the <strong>Schedule</strong> section in your sidebar to add 30-minute slots.</p>
              <p className="text-xs text-slate-500">Once you have at least one slot set, admin can activate your account for live classes.</p>
            </div>
          )}
          {!isStudent && current.id === 'done' && (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-2" strokeWidth={2.5} />
              <p className="text-slate-900 font-bold text-lg">Awaiting admin approval</p>
              <p className="text-sm text-slate-600 mt-1">Once admin verifies demo + onboarding + availability, you'll be activated for live classes.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex justify-between gap-2">
          <Button
            variant="outline"
            onClick={() => setStepIdx(Math.max(0, stepIdx - 1))}
            disabled={stepIdx === 0}
            className="nb-btn-warning"
            data-testid="onboarding-back"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Button onClick={handleNext} disabled={saving} className="nb-btn-primary" data-testid="onboarding-next">
            {stepIdx === steps.length - 1 ? 'Finish' : 'Next'} <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingWizard;
